// The catalog — the single source of truth for which games the hub knows about and
// which it PROMOTES (shows). The hub renders ONLY entries with `promoted: true`.
//
// To add a game: append an entry here. To promote it: set `promoted: true`.
// Nothing else needs to change — the grid is built from this list.
//
// Accents are drawn from each game's own --game-accent / --c-accent identity so a card
// reads as a faithful, miniature preview of the game it launches. `accent2` is optional;
// when present the identity mark uses a two-stop gradient.

export interface GameEntry {
  /** URL slug — the deploy folder name AND the catalog key. Href = `${GAME_BASE}${slug}/`. */
  slug: string;
  /** Display name (title-case, as the game brands itself). */
  name: string;
  /** Evocative one-liner, <= 60 chars, no spoilers. */
  tagline: string;
  /** Short "vibe" label shown as a chip (the register / feeling). */
  register: string;
  /** Primary card-identity accent (hex). */
  accent: string;
  /** Optional secondary accent for a two-stop identity mark (hex). */
  accent2?: string;
  /** Whether the hub shows this game. */
  promoted: boolean;
}

export const CATALOG: GameEntry[] = [
  // ---- PROMOTED (the playable set) ------------------------------------------------
  {
    slug: 'spectra',
    name: 'Spectra',
    tagline: 'One color chip breaks the hidden rule. Find it.',
    register: 'Color · calm',
    accent: '#7fd4e6',
    accent2: '#a6e3ef',
    promoted: true,
  },
  {
    slug: 'vault',
    name: 'Vault',
    tagline: 'Crack the lock by pure logic — every dial is forced.',
    register: 'Cerebral · minimal',
    accent: '#c9a24b',
    accent2: '#e8c77a',
    promoted: true,
  },
  {
    slug: 'wane',
    name: 'Wane',
    tagline: 'Flip a gear, predict the lamps, then run the movement.',
    register: 'Surreal · architectural',
    accent: '#d8b26a',
    accent2: '#f0d49a',
    promoted: true,
  },
  {
    slug: 'lumen',
    name: 'Lumen',
    tagline: 'Route the light through every window before dusk.',
    register: 'Lush · serene',
    accent: '#ffce7a',
    accent2: '#ffe6b0',
    promoted: true,
  },
  {
    slug: 'fold',
    name: 'Fold',
    tagline: 'Predict where the holes land once the paper folds.',
    register: 'Tactile · paper',
    accent: '#e0a23c',
    accent2: '#f0c478',
    promoted: true,
  },
  {
    slug: 'resonance',
    name: 'Resonance',
    tagline: 'Layer the tracks until the groove locks in.',
    register: 'Retro-neon · energetic',
    accent: '#ff4fb0',
    accent2: '#3fe0ff',
    promoted: true,
  },
  {
    slug: 'birdsong',
    name: 'Birdsong',
    tagline: 'Hear the one song that breaks the dawn pattern.',
    register: 'Cozy · dawn',
    accent: '#f2b45c',
    accent2: '#ffd79a',
    promoted: true,
  },
  {
    slug: 'ledger',
    name: 'Ledger',
    tagline: 'Fund each district to its line; the choice ripples.',
    register: 'Civic · numeric',
    accent: '#e8b54d',
    accent2: '#f2cf86',
    promoted: true,
  },
  {
    slug: 'drift',
    name: 'Drift',
    tagline: 'Slide the stars until the sky is secretly symmetric.',
    register: 'Night-sky · serene',
    accent: '#8fb6ff',
    accent2: '#ffe6a8',
    promoted: true,
  },
  {
    slug: 'tuesday',
    name: 'Tuesday',
    tagline: 'Watch a scene for ten seconds, then recall it.',
    register: 'Storybook · memory',
    accent: '#f0a868',
    accent2: '#ffc88c',
    promoted: true,
  },

  // ---- NOT PROMOTED (retired after QA; kept for reference, never rendered) ---------
  {
    slug: 'strata',
    name: 'Strata',
    tagline: 'Read the layers of an excavated cross-section.',
    register: 'Geological',
    accent: '#c08a4a',
    promoted: false,
  },
  {
    slug: 'cascade2',
    name: 'Plink',
    tagline: 'Drop the chip and watch the cascade settle.',
    register: 'Kinetic',
    accent: '#3fb6c9',
    promoted: false,
  },
  {
    slug: 'hearsay',
    name: 'Hearsay',
    tagline: 'Trace the rumor back through the village.',
    register: 'Storybook',
    accent: '#caa46a',
    promoted: false,
  },
  {
    slug: 'cairn',
    name: 'Cairn',
    tagline: 'Stack the stones until the balance holds.',
    register: 'Meditative',
    accent: '#8a90a8',
    promoted: false,
  },
];

/** The promoted subset, in catalog order — this is what the hub renders. */
export const PROMOTED: GameEntry[] = CATALOG.filter((g) => g.promoted);
